import { Object3D, CanvasTexture, SRGBColorSpace, PlaneGeometry, MeshBasicMaterial, Mesh, Vector3 } from "three";
import { config } from "./config";
import { TO_RAD, map } from "~/lib/utils.js";

export default class MilestoneTitle extends Object3D {
    constructor(title, milestoneIndex) {
        super();

        this.title = title;

        const id = `slt-js-milestone-title-${milestoneIndex}`;
        const ele = document.getElementById(id);
        if (!ele) {
            // console.error(`Element with id ${id} not found`);
            return;
        }

        // Get element dimensions
        const eleW = ele.offsetWidth;
        const eleH = ele.offsetHeight;
        this.eleW = eleW * config.pxToWorld;
        this.eleH = eleH * config.pxToWorld;

        // Create an offscreen canvas with proper dimensions
        const canvas = document.createElement("canvas");
        canvas.width = eleW;
        canvas.height = eleH;

        const ctx = canvas.getContext("2d", { willReadFrequently: true });

        // Make background transparent
        ctx.clearRect(0, 0, eleW, eleH);

        // Clone and style our content
        this.renderElementToCanvas(ele, ctx);

        // Create texture from canvas
        const texture = new CanvasTexture(canvas);
        texture.colorSpace = SRGBColorSpace;

        // Create 3D plane with texture
        const geometry = new PlaneGeometry(this.eleW, this.eleH);
        const material = new MeshBasicMaterial({
            map: texture,
            transparent: true,
            depthWrite: false,
        });

        this.mesh = new Mesh(geometry, material);
        this.updateLayout();

        this.add(this.mesh);
    }

    renderElementToCanvas(element, ctx) {
        // Get computed styles
        const styles = window.getComputedStyle(element);

        // Apply styles to match original element
        const fontSize = parseFloat(styles.fontSize);
        const fontFamily = styles.fontFamily;
        const fontWeight = styles.fontWeight;
        const letterSpacing = styles.letterSpacing;

        ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
        ctx.strokeStyle = "none";
        ctx.letterSpacing = letterSpacing;

        let currentX = 0;

        // For text elements, render the text content
        if (element.children.length === 2) {
            Array.from(element.children).forEach((child) => {
                // Get text measurements
                const text = child.textContent;
                const textW = child.offsetWidth;
                const textH = child.offsetHeight;

                // Set color and draw text
                const color = window.getComputedStyle(child).color;
                ctx.fillStyle = color;

                // Position text (centered vertically)
                const x = currentX;
                const y = textH;
                ctx.fillText(text, x, y);

                currentX += textW;
            });
        }
    }

    // New method to calculate angle based on window width
    calculateAngle(windowWidth) {
        return map(windowWidth, 1920, 393, Math.PI / 6, Math.PI / 2.8);
    }

    // New method to apply position and rotation based on angle
    applyTransform(angle) {
        const radius = config.lines.radius;
        const yAxis = new Vector3(0, 1, 0); // Y axis
        const zAxis = new Vector3(0, 0, 1); // Z axis

        // Reset rotation before applying new ones
        this.mesh.rotation.set(0, 0, 0);
        this.mesh.position.set(0, 0, 0); // reset position before calculating new one

        this.mesh.rotateOnAxis(zAxis, angle); // rotate around Z axis
        this.mesh.rotateOnAxis(yAxis, 90 * TO_RAD); // rotate 90 degrees, so the object faces the screen
        this.mesh.position.x = -Math.cos(angle) * radius;
        this.mesh.position.y = -Math.sin(angle) * radius;
    }

    // Method called on init and resize
    updateLayout(windowWidth = window.innerWidth) {
        // Default to current width
        const angle = this.calculateAngle(windowWidth);
        this.applyTransform(angle);
    }

    destroy() {
        // Dispose of all child meshes and their resources
        this.traverse((object) => {
            if (object.geometry) {
                object.geometry.dispose();
            }

            if (object.material) {
                if (object.material.map) {
                    object.material.map.dispose();
                }
                object.material.dispose();
            }
        });

        // Clear all children
        while (this.children.length > 0) {
            this.remove(this.children[0]);
        }
    }
}
